// CreatorProductsPanel.tsx — produits d'un créateur, dans l'onglet Ressources de sa fiche
//
// Même présentation que les onglets du Dashboard (barre de filtres, bascule cartes / liste,
// mêmes cartes, même liste). Image du produit quand elle existe, sinon son nom sur une couleur.
// Données : admin-api AdminResourceItems (kind = products).

import React, { useEffect, useMemo, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import extra  from './VideosSection.module.scss';
import { ListTable, TitleCell, WhenCell, Dash } from './DashboardList';
import { Segment, ViewToggle, useView, plural, tintOf } from './dashboardShared';
import { WWW_URLS } from '../../../backendServices/urls';
import { getAdminResourceItems, AdminResourceItem } from '../../../backendServices/adminManagment/api';

const VIEW_KEY = 'adminResources.productsView';

type Sort = 'recent' | 'name' | 'price';
const SORTS: readonly (readonly [Sort, string])[] = [ [ 'recent', 'Plus récents' ], [ 'name', 'Nom (A → Z)' ], [ 'price', 'Plus chers' ] ];
const byDateDesc = (a: AdminResourceItem, b: AdminResourceItem) => (b.date ?? '').localeCompare(a.date ?? '');
const COMPARE: Record<Sort, (a: AdminResourceItem, b: AdminResourceItem) => number> = {
  recent: byDateDesc,
  name  : (a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base', numeric: true }),
  price : (a, b) => (b.priceTtc ?? -1) - (a.priceTtc ?? -1) || byDateDesc(a, b),
};

const euros = (cents: number | null | undefined) => cents == null ? '' : `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const imageOf = (p: AdminResourceItem) => p.publicRef && p.image ? `${WWW_URLS.MediaServer}${p.publicRef}/images/thumbnail.webp` : '';

/** L'image du produit ; à défaut (ou si le fichier manque), son nom sur une couleur. */
const Pic: React.FC<{ p: AdminResourceItem; mini?: boolean }> = ({ p, mini }) => {
  const [ failed, setFailed ] = useState(false);
  const src = imageOf(p);
  const img = src && !failed ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} /> : null;
  if (mini) return <div style={{ width: '100%', height: '100%', background: tintOf(p.id) }}>{img}</div>;
  return (
    <div className={styles.thumb} style={{ background: tintOf(p.id), opacity: p.active === false ? 0.7 : 1 }}>
      {img ?? <span className={styles.thumbName}>{p.title}</span>}
      <span className={styles.badges} style={{ zIndex: 1, position: 'absolute' }}>
        <span className={`${styles.badge} ${p.active ? styles.badgeOn : styles.badgeOff}`}>{p.active ? 'ACTIF' : 'INACTIF'}</span>
      </span>
    </div>
  );
};

const StateBadge: React.FC<{ p: AdminResourceItem }> = ({ p }) =>
  <span className={`${styles.badge} ${p.active ? styles.badgeOn : styles.badgeOff}`}>{p.active ? 'ACTIF' : 'INACTIF'}</span>;

const CreatorProductsPanel: React.FC<{ adminId: number }> = ({ adminId }) => {

  const [ products, setProducts ] = useState<AdminResourceItem[]>([]);
  const [ loading , setLoading  ] = useState(true);
  const [ error   , setError    ] = useState(false);
  const [ state   , setState    ] = useState<'all' | 'on' | 'off'>('all');
  const [ kind    , setKind     ] = useState<string>('all');
  const [ query   , setQuery    ] = useState('');
  const [ sort    , setSort     ] = useState<Sort>('recent');
  const [ view    , setView     ] = useView(VIEW_KEY);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    getAdminResourceItems(adminId, 'products').then(r => {
      if (cancelled) return;
      if (r.code === 1) setProducts(r.payload.items); else setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [adminId]);

  const kinds = useMemo(() => {
    const count = new Map<string, number>();
    products.forEach(p => { if (p.kind) count.set(p.kind, (count.get(p.kind) ?? 0) + 1); });
    return Array.from(count.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);
  }, [products]);

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter(p => (kind === 'all' || p.kind === kind) && (!q || `${p.title} ${p.code ?? ''}`.toLowerCase().includes(q)));
  }, [products, kind, query]);

  const shown = useMemo(() => matching.filter(p => state === 'all' || (state === 'on') === !!p.active).sort(COMPARE[sort]), [matching, state, sort]);

  const on = matching.filter(p => p.active).length;

  if (loading) return <span>Chargement des produits…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (products.length === 0) return <div className={styles.empty}>Aucun produit.</div>;

  const filtersOn = query.trim() !== '' || state !== 'all' || kind !== 'all';

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Nom, code…" aria-label="Rechercher un produit"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', `Tous ${matching.length}`], ['on', `Actifs ${on}`], ['off', `Inactifs ${matching.length - on}`] ] as const} />
        {kinds.length > 1 &&
          <Segment label="Type" value={kind} onChange={setKind}
                   choices={[ ['all', 'Tous les types'] as const, ...kinds.map(k => [k, k] as const) ]} />}
        {filtersOn && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'produit')}</strong> sur {products.length}</span>
          <button type="button" className={extra.reset} onClick={() => { setQuery(''); setState('all'); setKind('all'); }}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {shown.length === 0 && <div className={styles.empty}>Aucun produit ne correspond.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(p => (
            <article key={p.id} className={styles.card}>
              <Pic p={p} />
              <div className={styles.cardBody}>
                <span className={styles.cardName} title={p.title}>{p.title || '—'}</span>
                <div className={styles.chips}>
                  {p.kind && <span>{p.kind}</span>}
                  {p.priceTtc != null && <span>{euros(p.priceTtc)} TTC</span>}
                  {p.priceHt != null && <span>{euros(p.priceHt)} HT</span>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={p => p.id} dateOf={p => p.date}
                   noun={{ one: 'produit', many: 'produits' }}
                   sort={sort === 'recent' ? null : sort} onSort={id => setSort((id ?? 'recent') as Sort)}
                   columns={[
          { id: 'name', label: 'Produit', sort: COMPARE.name, cell: p =>
              <TitleCell thumb={<Pic p={p} mini />} title={p.title || '—'} titleHint={p.title} sub={p.code ?? ''} /> },
          { id: 'state', label: 'État', cell: p => <StateBadge p={p} /> },
          { id: 'kind', label: 'Type', cell: p => p.kind || <Dash/> },
          { id: 'price', label: 'Prix TTC', right: true, sort: COMPARE.price, cell: p => euros(p.priceTtc) || <Dash/> },
          { id: 'ht', label: 'Prix HT', right: true, hide: 'md', cell: p => euros(p.priceHt) || <Dash/> },
          { id: 'date', label: 'Créé', cell: p => <WhenCell iso={p.date} /> },
        ]} />
      )}
    </div>
  );
};

export default CreatorProductsPanel;
